'use client';

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppDispatch } from '../../redux/Store';
import { setActiveFilter, setActiveSort, setSelectedProfile } from '../../redux/slices/marketplaceSlice';
import { QuickFilter, SortOption, LocationScope } from '../../redux/slices/marketplaceSlice';

// Valid values for URL param validation. Mirrors the slice's union types
// — if those grow, add to these arrays too.
const VALID_TABS = ['all', 'previously-owned'] as const;
const VALID_FILTERS: readonly QuickFilter[] = ['spotlight', 'all', 'top', 'cheapest', 'most-bought', 'least-bought'];
const VALID_SORTS: readonly SortOption[] = ['highest-value', 'lowest-value', 'trending', 'most-popular'];
const VALID_SCOPES: readonly LocationScope[] = ['global', 'home', 'touring'];
const SCOPE_LABELS: { scope: LocationScope; label: string; hint: string }[] = [
  { scope: 'home',    label: 'Home',    hint: 'Profiles by creators in your home location' },
  { scope: 'touring', label: 'Touring', hint: 'Profiles by creators in or visiting your touring location' },
  { scope: 'global',  label: 'Global',  hint: 'Profiles from anywhere on Klyx' },
];
import { fetchMarketplaceProfiles, fetchUserBalance, purchaseProfile } from '../../redux/thunks/marketplaceThunks';
import {
  selectMarketplaceProfiles,
  selectActiveFilter,
  selectActiveSort,
  selectUserBalance,
  selectMarketplaceLoading,
  selectMarketplacePagination,
  selectSelectedProfile,
} from '../../redux/selectors/marketplaceSelectors';
import { useSelector } from 'react-redux';
import FilterBar from './components/FilterBar';
import SortControls from './components/SortControls';
import ProfileGrid from './components/ProfileGrid';
import { ProfileListing } from './components/ProfileCard';
import UserBalanceDisplay from './components/UserBalanceDisplay';
import LoadMoreButton from './components/LoadMoreButton';
import PurchaseModal from './components/PurchaseModal';
import PreviouslyOwnedSection, { usePreviouslyOwned, PreviouslyOwnedProfile } from './components/PreviouslyOwnedSection';
import './Marketplace.scss';

type MarketplaceTab = 'all' | 'previously-owned';

const MarketplacePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const profiles = useSelector(selectMarketplaceProfiles);
  const activeFilter = useSelector(selectActiveFilter);
  const activeSort = useSelector(selectActiveSort);
  const userBalance = useSelector(selectUserBalance);
  const isLoading = useSelector(selectMarketplaceLoading);
  const pagination = useSelector(selectMarketplacePagination);
  const selectedProfile = useSelector(selectSelectedProfile);

  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  // Location scope is page-local (not in Redux) — it drives the
  // discovery scope toggle and persists in the URL like tab/filter/sort.
  // availableScopes and effectiveScope come back from the server in
  // the fetch response; we mirror them so the toggle UI can hide
  // unavailable options and the downgrade hint stays in sync.
  const [activeScope, setActiveScope] = useState<LocationScope>('global');
  const [availableScopes, setAvailableScopes] = useState<LocationScope[]>(['global']);
  const [effectiveScope, setEffectiveScope] = useState<LocationScope>('global');
  const [scopeArea, setScopeArea] = useState<{ countryCode: string; admin1: string | null } | null>(null);

  // Tab / filter / sort persist in the URL. Tab is local state (UI
  // chrome only); filter + sort hydrate Redux on mount so the existing
  // Redux-driven fetch flow keeps working.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const activeTab: MarketplaceTab =
    (VALID_TABS as readonly string[]).includes(tabParam ?? '')
      ? (tabParam as MarketplaceTab)
      : 'all';

  const setActiveTab = (tab: MarketplaceTab) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (tab === 'all') next.delete('tab');
        else next.set('tab', tab);
        return next;
      },
      { replace: true },
    );
  };

  // Drives both the "Previously Owned" tab visibility and the tab body itself.
  const previouslyOwned = usePreviouslyOwned();
  const previouslyOwnedCount = previouslyOwned.profiles.length;
  const showPreviouslyOwnedTab = !previouslyOwned.loading && previouslyOwnedCount > 0;

  // Mount: hydrate Redux from URL params (if present + valid), then fire
  // the initial fetch with the resolved values. Doing it inline avoids
  // the double-fetch you'd get from separate hydrate/fetch effects.
  useEffect(() => {
    const urlFilter = searchParams.get('filter');
    const urlSort = searchParams.get('sort');
    const urlScope = searchParams.get('scope');
    const filter: QuickFilter =
      urlFilter && (VALID_FILTERS as readonly string[]).includes(urlFilter)
        ? (urlFilter as QuickFilter)
        : activeFilter;
    const sort: SortOption =
      urlSort && (VALID_SORTS as readonly string[]).includes(urlSort)
        ? (urlSort as SortOption)
        : activeSort;
    // Default scope is 'global' on first visit (no URL param) — same as
    // the leaderboard page. Users explicitly opt into a scoped view, so
    // initial state can't briefly look like a stale "you haven't set a
    // home location" warning while data loads.
    const scope: LocationScope =
      urlScope && (VALID_SCOPES as readonly string[]).includes(urlScope)
        ? (urlScope as LocationScope)
        : 'global';
    if (filter !== activeFilter) dispatch(setActiveFilter(filter));
    if (sort !== activeSort) dispatch(setActiveSort(sort));
    setActiveScope(scope);
    setEffectiveScope(scope);
    runFetch({ filter, sort, scope, page: 1 });
    dispatch(fetchUserBalance());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only
  }, [dispatch]);

  // Wraps fetchMarketplaceProfiles + syncs scope info from the response.
  // Centralized so every fetch-triggering handler stays in sync without
  // duplicating the unwrap-and-set-scope dance.
  const runFetch = async (opts: { filter: QuickFilter; sort: SortOption; scope: LocationScope; page: number; append?: boolean }) => {
    // Optimistically reset effectiveScope so a stale value from a prior
    // fetch can't briefly trigger the downgrade hint while the new
    // request is in flight.
    setEffectiveScope(opts.scope);
    try {
      const data = await dispatch(fetchMarketplaceProfiles(opts)).unwrap();
      if (data.scope) setEffectiveScope(data.scope);
      if (data.availableScopes) setAvailableScopes(data.availableScopes);
      setScopeArea(data.scopeArea ?? null);
    } catch {
      // Errors surface via the slice's error state; nothing extra here.
    }
  };

  // Helper: patch a single search param (or remove when it matches the
  // default). Used by filter/sort handlers so the URL reflects current
  // state. `replace` so clicking through options doesn't pile history.
  const patchSearchParam = (key: string, value: string | null, defaultValue: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (value === null || value === defaultValue) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };

  const handleFilterChange = (filter: QuickFilter) => {
    // Some tab names imply a natural sort (Cheapest = lowest price).
    // The server used to enforce these implicitly which silently
    // overrode the sort dropdown — that's been removed. Instead, when
    // the user lands on a tab whose name implies an order, we set the
    // sort to match. They can still pick a different sort after.
    const impliedSort: Partial<Record<QuickFilter, SortOption>> = {
      cheapest: 'lowest-value',
      'most-bought': 'most-popular',
    };
    const nextSort = impliedSort[filter] ?? activeSort;
    dispatch(setActiveFilter(filter));
    if (nextSort !== activeSort) {
      dispatch(setActiveSort(nextSort));
      patchSearchParam('sort', nextSort, 'highest-value');
    }
    runFetch({ filter, sort: nextSort, scope: activeScope, page: 1 });
    patchSearchParam('filter', filter, 'all');
  };

  const handleSortChange = (sort: SortOption) => {
    dispatch(setActiveSort(sort));
    runFetch({ filter: activeFilter, sort, scope: activeScope, page: 1 });
    patchSearchParam('sort', sort, 'highest-value');
  };

  const handleScopeChange = (scope: LocationScope) => {
    setActiveScope(scope);
    runFetch({ filter: activeFilter, sort: activeSort, scope, page: 1 });
    patchSearchParam('scope', scope, 'global');
  };

  const handleBuyClick = (profile: ProfileListing) => {
    dispatch(setSelectedProfile(profile));
    setShowPurchaseModal(true);
  };

  // "Buy Back" reuses the same purchase modal flow as the main grid. The
  // previously-owned endpoint returns a slimmer shape than ProfileListing —
  // map the fields we know and default the rest (badges/spotlight are not
  // surfaced on the trade-history response).
  const handleBuyBack = (p: PreviouslyOwnedProfile) => {
    const listing: ProfileListing = {
      id: p.id,
      creatorId: p.creatorId,
      creatorUsername: p.creatorUsername,
      creatorDisplayName: p.creatorDisplayName,
      creatorAvatarUrl: p.creatorAvatarUrl ?? undefined,
      ownerId: p.currentOwnerId,
      ownerUsername: p.currentOwnerUsername,
      ownerDisplayName: p.currentOwnerDisplayName,
      ownerAvatarUrl: p.currentOwnerAvatarUrl ?? undefined,
      price: p.currentPrice,
      klyxValue: p.currentPrice,
      trendingScore: 0,
      purchaseCount: p.timesSold,
      creatorLocation: null,
      badges: {},
      spotlight: null,
    };
    handleBuyClick(listing);
  };

  const handleClosePurchaseModal = () => {
    setShowPurchaseModal(false);
    dispatch(setSelectedProfile(null));
  };

  const handleConfirmPurchase = async (profile: ProfileListing, customPrice: number) => {
    setIsProcessing(true);
    try {
      console.log('Purchasing profile:', profile.ownerDisplayName, 'at price:', customPrice);
      // Call purchase thunk with custom price
      await dispatch(
        purchaseProfile({ profileId: profile.id, customPrice })
      ).unwrap();

      toast.success(`Successfully purchased ${profile.creatorDisplayName || profile.creatorUsername} for ${customPrice.toFixed(6)} KLV`);
      // Refresh the previously-owned list so a buy-back removes the card,
      // and a freshly-sold profile shows up if the user owned it before.
      previouslyOwned.refetch();
      handleClosePurchaseModal();
    } catch (error: any) {
      console.error('Purchase failed:', error);
      const errorMessage = error?.message || error || 'Purchase failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleLoadMore = () => {
    const nextPageNum = pagination.currentPage + 1;
    runFetch({
      filter: activeFilter,
      sort: activeSort,
      scope: activeScope,
      page: nextPageNum,
      append: true, // Append to existing profiles
    });
  };

  return (
    <div className="marketplace-page">
      <div className="marketplace-container">
        <header className="marketplace-header">
          <div className="marketplace-header-content">
            <h1>Marketplace</h1>
            <p>Discover and purchase profiles and more to come!</p>
          </div>
          <UserBalanceDisplay balance={userBalance} loading={false} />
        </header>

        {/* Tab Switcher — "Previously Owned" only renders when the user has sold something */}
        {showPreviouslyOwnedTab && (
          <div className="marketplace-tabs" role="tablist">
            <button
              role="tab"
              aria-selected={activeTab === 'all'}
              className={`marketplace-tab ${activeTab === 'all' ? 'active' : ''}`}
              onClick={() => setActiveTab('all')}
            >
              All Profiles
            </button>
            <button
              role="tab"
              aria-selected={activeTab === 'previously-owned'}
              className={`marketplace-tab ${activeTab === 'previously-owned' ? 'active' : ''}`}
              onClick={() => setActiveTab('previously-owned')}
            >
              Previously Owned
              <span className="marketplace-tab__count">{previouslyOwnedCount}</span>
            </button>
          </div>
        )}

        {activeTab === 'all' ? (
          <>
            {/* Location scope — Home / Touring / Global. Each button
                appears only when the server says the caller can use it. */}
            <div className="marketplace-scope" role="tablist" aria-label="Scope">
              {SCOPE_LABELS.filter((opt) => availableScopes.includes(opt.scope)).map((opt) => (
                <button
                  key={opt.scope}
                  type="button"
                  role="tab"
                  aria-selected={opt.scope === activeScope}
                  title={opt.hint}
                  className={`marketplace-scope__btn ${opt.scope === activeScope ? 'active' : ''}`}
                  onClick={() => handleScopeChange(opt.scope)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
            {/* Show the active scope area ("Showing: Massachusetts, US")
                so the user knows what's being filtered when they're not
                on Global. Cards still display city — only the filter
                widens to country+state. */}
            {!isLoading && effectiveScope !== 'global' && scopeArea && (
              <p className="marketplace-scope-area">
                Showing: {[scopeArea.admin1, scopeArea.countryCode].filter(Boolean).join(', ')}
              </p>
            )}
            {/* Downgrade hint — gated on !isLoading so a previous fetch's
                effectiveScope can't briefly flash a stale warning while
                a new request is in flight. */}
            {!isLoading && activeScope !== 'global' && effectiveScope === 'global' && (
              <p className="marketplace-scope-hint">
                You haven't set a {activeScope} location yet — showing global profiles.
              </p>
            )}

            {/* Filters and Sort Controls */}
            <div className="marketplace-controls">
              <FilterBar activeFilter={activeFilter} onFilterChange={handleFilterChange} />
              <SortControls activeSort={activeSort} onSortChange={handleSortChange} />
            </div>

            {/* Profile Grid */}
            <div className="marketplace-content">
              <ProfileGrid profiles={profiles} loading={isLoading} onBuyClick={handleBuyClick} />

              {/* Load More Button */}
              {!isLoading && profiles.length > 0 && (
                <LoadMoreButton
                  onClick={handleLoadMore}
                  loading={isLoading}
                  hasMore={pagination.hasMore}
                />
              )}
            </div>
          </>
        ) : (
          <div className="marketplace-content">
            <PreviouslyOwnedSection
              profiles={previouslyOwned.profiles}
              loading={previouslyOwned.loading}
              error={previouslyOwned.error}
              onBuyBack={handleBuyBack}
            />
          </div>
        )}
      </div>

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <PurchaseModal
          profile={selectedProfile}
          userBalance={userBalance}
          onClose={handleClosePurchaseModal}
          onConfirm={handleConfirmPurchase}
          isProcessing={isProcessing}
        />
      )}
    </div>
  );
};

export default MarketplacePage;
